<template>
  <div class="workspace__container__view">
    <div class="workspace__container__view__header" v-if="!notDatesAvailable">
      <div class="workspace__container__view__header__actions">
        <h2>{{(resultsFiltered && resultsFiltered.stationName) || 'Station'}}</h2>
      </div>
    </div>
    <template v-if="successResult">
      <div class="generate--info" v-if="notDatesAvailable">
        <div>
          <i class="material-icons">info</i>{{ $t('workspace.spot_test.none_result') }}
        </div>
      </div>
      <div class="spot-test__container" v-else :class="{'with-filter': daySelect != ''}">
        <div class="spot-test__container__buttons">
          <ul>
            <li>
              <div class="left-menu__layout__button">
                <button :class="{'active': daySelect != ''}" @click="changeDateFiltered('')">{{$t('workspace.spot_test.overview')}}</button>
              </div>
            </li>
            <li v-for="day in datesAvailable" :key="day">
              <div class="left-menu__layout__button">
                <button :class="{'active': daySelect != day}" @click="changeDateFiltered(day)">{{ formatDate(day) }}</button>
              </div>
            </li>
          </ul>
        </div>
        <div class="spot-test__container__table" v-if="resultsFiltered.errors">
          <div class="generate--success" v-if="resultsFiltered.errors.length == 0">
            <div>
              <i class="material-icons">check_circle</i>{{$t('workspace.spot_test.generate_report')}}
            </div>
          </div>
          <div class="table-scroll" v-else>
            <table class="workspace__container__view__table">
              <thead>
                <tr>
                  <th>{{ $t("workspace.spot_test.date") }}</th>
                  <th>{{ $t("workspace.spot_test.hour") }}</th>
                  <th>{{ $t("workspace.spot_test.spotName") }}</th>
                  <th>{{ $t("workspace.spot_test.errorCode") }}</th>
                  <th class="column-m center">{{ $t("workspace.spot_test.actions") }}</th>
                </tr>
              </thead>
              <tbody v-if="Array.isArray(resultsFiltered.errors)">
                <tr v-for="result in resultsFiltered.errors" :key="result.SpotId">
                  <td>{{ formatDate(result.Date) }}</td>
                  <td>{{ result.Hour }}</td>
                  <td>{{ result.SpotName }}</td>
                  <td :title="$t(`workspace.spot_test.listErrors.${result.ErrorCode}_tooltip`)">{{ $t(`workspace.spot_test.listErrors.${result.ErrorCode}`) }}</td>
                  <td class="column-m center"><i class="material-icons" @click="openInNewTab(result)" style="cursor:pointer">open_in_new</i></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div class="spot-test__container__graphics" v-if="daySelect == ''">
          <div class="spot-test__container__graphics__container">
            <div class="spot-test__container__graphics__container__header">
              <strong>{{$t(`workspace.spot_test.chart_least`)}}</strong>
              <div class="settings-input">
                <select v-model="dateWithLeastSeconds" @change="changeDateChart('chartLeast', dateWithLeastSeconds)">
                  <option v-for="day in datesAvailable" :key="day" :value="day">{{formatDate(day)}}</option>
                </select> <!-- No -->
              </div>
            </div>
            <canvas id="chartLeast" ref="chartLeast"></canvas>
          </div>
          <div class="spot-test__container__graphics__container">
            <div class="spot-test__container__graphics__container__header">
              <strong>{{$t(`workspace.spot_test.chart_more`)}}</strong>
              <div class="settings-input">
                <select v-model="dateWithMoreSeconds" @change="changeDateChart('chartMore', dateWithMoreSeconds)">
                  <option v-for="day in datesAvailable" :key="day" :value="day">{{formatDate(day)}}</option>
                </select> <!-- No -->
              </div>
            </div>
            <canvas id="chartMore" ref="chartMore"></canvas>
          </div>
        </div>
        <div class="spot-test__container__graphics" v-else>
          <div class="spot-test__container__graphics__container">
            <canvas id="chartDetail" ref="chartDetail"></canvas>
          </div>
        </div>
      </div>
    </template>
    <template v-else>
      <div class="generate--error">
        <div>
          <i class="material-icons">info</i>{{ $t('workspace.spot_test.error_result') }}
        </div>
      </div>
    </template>
  </div>
</template>
<script>
import { ref } from '@vue/reactivity';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { computed, onMounted, onUnmounted, watch } from '@vue/runtime-core';
import moment from 'moment';
import Chart from 'chart.js/auto';
import { useI18n } from 'vue-i18n';

export default {
  setup() {
    const store = useStore();
    const route = useRoute();
    const router= useRouter();
    const { t } = useI18n();
    const results = ref({});
    const resultsFiltered = ref({});
    let intervalCall = null;
    const tab = computed(() => store.getters.tabs_getActive);
    const datesAvailable = ref([]);
    const daySelect = ref('');
    const dateWithLeastSeconds = ref();
    const dateWithMoreSeconds = ref();
    const notDatesAvailable = ref(false);
    const successResult = ref(true);

    // Charts
    let chartLeast, chartMore, chartDetail;


    const backgroundColor = function(val, max, ctx) {
      const colors = {
        green: '#4caf50',
        yellow: '#ffeb3b',
        orange: '#ff5722',
        red: '#ff3122'
      };
      const p = Math.round(val * 100 / max);
      const gradient = ctx.createLinearGradient(0, 0, 0, 300);

      if (p >= 90) {
        gradient.addColorStop(0, colors.red);
      } else if (p >= 70) {
        gradient.addColorStop(0, colors.orange);
      } else if (p >= 50) {
        gradient.addColorStop(0, colors.yellow);
      } else {
        gradient.addColorStop(0, colors.green);
      }

      gradient.addColorStop(1, colors.green);
      return gradient;
    };

    onMounted(() => {
      loadComponent();
    });

    onUnmounted(() => {
      if (intervalCall) clearInterval(intervalCall);
    });

    async function loadComponent() {
      store.dispatch('loading_setActive', true);

      if (route.name == 'SPOT_TEST') {
        try {
          await getGenerationResults();
        } catch (error) {
          intervalCall = setInterval(async () => {
            await getGenerationResults();
          }, 3000);
        }
      } else if (route.name == 'SPOT_REPORT') {
        const model = {...route.params}
        const testResult = await store.dispatch('trafficLog_getGenerationReport', model);
        results.value = testResult;
        successResult.value = testResult.success;
        if (Object.keys(results.value.commercialBreakSecondsPerHour).length > 0) {
          datesAvailable.value = Object.keys(results.value.commercialBreakSecondsPerHour).map(date => date);
          results.value.errors = JSON.parse(results.value.errors);
          changeDateFiltered('');
        } else {
          notDatesAvailable.value = true;
        }
        store.dispatch('loading_setActive', false);
        store.dispatch('loading_setCounter', {active: false});
      }
    }

    async function getGenerationResults () {
      const testResult = await store.dispatch('trafficLog_getGenerationResults', route.params.id);
      return new Promise((resolve, reject) => {
        if (typeof testResult === 'object' && testResult !== null) {
          results.value = testResult;
          successResult.value = testResult.success;
          if (intervalCall) clearInterval(intervalCall);
          if (Object.keys(results.value.commercialBreakSecondsPerHour).length > 0) {
            datesAvailable.value = Object.keys(results.value.commercialBreakSecondsPerHour).map(date => date);
            results.value.errors = JSON.parse(results.value.errors);
            changeDateFiltered('');
          } else {
            notDatesAvailable.value = true;
          }
          store.dispatch('tabs_updateTab', {id: route.params.id, extendedData: {stationName: results.value.stationName}});
          store.dispatch('loading_setActive', false);
          store.dispatch('loading_setCounter', {active: false});
          resolve();
        } else {
          reject();
        }
      })
    }

    function generateChart(ctx, values, max) {
      const modelValues = values.map((value, index) => { return {'hour': `${index < 10 ? '0':''}${index}`, 'count': value} });
      return new Chart(
        ctx,
        {
          data: {
            datasets: [{
                type: 'bar',
                label: t('workspace.spot_test.graph_minutes'),
                data: modelValues.map(row => (row.count / 60)),
                backgroundColor: modelValues.map(row => backgroundColor((row.count / 60), (max / 60), ctx)),
                borderRadius: 4
            }, {
                type: 'line',
                label: 'Max.',
                data: Array.from(Array(24), () => (max / 60)),
                backgroundColor: 'tomato',
                borderColor: 'tomato'
            }],
            labels: modelValues.map(row => row.hour),
            options: {
              responsive: true,
              labels: false
            }
          },
        }
      );
    }
    
    function formatDate(date) {
      return moment(date).format("YYYY-MM-DD")
    }

    function changeDateFiltered(date) {
      daySelect.value = date;
      if (date == '') {
        resultsFiltered.value = {...results.value};
        // Generate array days available
        dateWithLeastSeconds.value = results.value.dateWithLeastCommercialBreakSeconds;
        dateWithMoreSeconds.value = results.value.dateWithSecondsClosestToMax;

        // Generate charts
        if (chartLeast) chartLeast.destroy();
        if (chartMore) chartMore.destroy();
        setTimeout(() => {
          chartLeast = generateChart(document.getElementById('chartLeast').getContext('2d'), JSON.parse(results.value.commercialBreakSecondsPerHour[dateWithLeastSeconds.value]), results.value.commercialBreakMaxTime);
          chartMore = generateChart(document.getElementById('chartMore').getContext('2d'), JSON.parse(results.value.commercialBreakSecondsPerHour[dateWithMoreSeconds.value]), results.value.commercialBreakMaxTime);
        })
      } else {
        resultsFiltered.value = {
          "stationName": results.value.stationName,
          "commercialBreakMaxTime": results.value.commercialBreakMaxTime,
          "errors": [...results.value.errors].filter(result => result.Date == date),
          'commercialBreakSecondsPerHour': {...results.value.commercialBreakSecondsPerHour},
          'dateWithLeastCommercialBreakSeconds': '',
          'dateWithSecondsClosestToMax': ''
        };
        if (chartDetail) chartDetail.destroy();
        setTimeout(() => {
          chartDetail = generateChart(document.getElementById('chartDetail').getContext('2d'), JSON.parse(resultsFiltered.value.commercialBreakSecondsPerHour[date]), results.value.commercialBreakMaxTime);
        })
      }
    }

    function changeDateChart(chart, date) {
      if (chart == 'chartLeast') {
        if (chartLeast) chartLeast.destroy();
        setTimeout(() => {
          chartLeast = generateChart(document.getElementById(chart).getContext('2d'), JSON.parse(results.value.commercialBreakSecondsPerHour[date]), results.value.commercialBreakMaxTime);
        })
      } else {
        if (chartMore) chartMore.destroy();
        setTimeout(() => {
          chartMore = generateChart(document.getElementById(chart).getContext('2d'), JSON.parse(results.value.commercialBreakSecondsPerHour[date]), results.value.commercialBreakMaxTime);
        })
      } 
    }

    async function openInNewTab(campaign) {
      const tab = {
        id: campaign.SpotId,
        module: 'spot_programming',
        name: 'spot/detail',
        extendedData: {}
      }
       
      await store.dispatch('tabs_addTab', tab);
      await store.dispatch('tabs_setActive', tab);
      router.push(`/${tab.module}/${tab.name}/${tab.id}`);
    }

    watch(() => route.params.id, () => {
      if (intervalCall) clearInterval(intervalCall);
      loadComponent();
    });

    return {
      resultsFiltered,
      datesAvailable,
      daySelect,
      dateWithLeastSeconds,
      dateWithMoreSeconds,
      formatDate,
      tab,
      changeDateFiltered,
      changeDateChart,
      openInNewTab,
      notDatesAvailable,
      successResult
    }
  }
}
</script>
<style lang="scss" scoped>
  @import '@/styles/_common.scss';

  h2 {
    display: flex;
    gap: 8px;
  }
  .generate--success {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
    font-size: 20px;
    // background: #4caf50;
    
    div {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 8px;
      height: fit-content;
      padding: 24px;
      border: 1px solid #4caf50;
      background: #4caf4f25;
      border-radius: 8px;

      i {
        color: #4caf50;
      }
    }
  }

  .generate--info {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
    font-size: 20px;
    // background: #4caf50;
    
    div {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 8px;
      height: fit-content;
      padding: 24px;
      color: black;
      border: 1px solid #669df6;
      background: #d2e3fc;
      border-radius: 8px;

      i {
        color: #669df6;
      }
    }
  }

  .generate--error {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
    font-size: 20px;
    
    div {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 8px;
      height: fit-content;
      padding: 24px;
      color: black;
      border: 1px solid #f82f2f;
      background: #fcd2d2;
      border-radius: 8px;

      i {
        color: #f66666;
      }
    }
  }

  .spot-test__container {
    height: calc(100vh - 242px);
    width: 100%;
    display: grid;
    grid-gap: 24px;
    grid-template:
      "buttons tableResult" 
      "buttons graphics";
    grid-template-columns: 200px auto;
    grid-template-rows: 280px auto;
    &.with-filter {
      .spot-test__container__graphics {
        grid-template-columns: 1fr!important;

        &__container {
          width: 50%;
          margin: 0 auto;
        }
      }
    }
    
    &__buttons {
      grid-area: buttons;
    
      ul {
        margin: 0;
        padding: 0;
        list-style: none;
        display: flex;
        flex-direction: column;
        gap: 8px;
        max-height: 700px;
        overflow-y: auto;
        @include vertical-scrollbar;

        button {
          width: 100%;
          opacity: .7;

          &.active {
            opacity: 1;
          }
        }
      }
    }

    &__table {
      grid-area: tableResult;
      .table-scroll {
        height: 280px;
      }
    }

    @mixin vertical-scrollbar { 
      &::-webkit-scrollbar {
        width: 12px;
        cursor: pointer;
      }
      
      &::-webkit-scrollbar-track {
        -webkit-box-shadow: inset 0 0 6px rgba(0,0,0,0.3);
      }
      
      &::-webkit-scrollbar-thumb {
        background-color: #606060;
        outline: 1px solid rgba(32,32,32,.7);
        border-radius: 4px;
      }
    }

    &__graphics {
      height: 100%;
      grid-area: graphics;
      display: grid;
      grid-template-columns: 1fr 1fr;
      // justify-content: space-between;
      grid-gap: 24px;
      // overflow-y: auto;
      // @include vertical-scrollbar;
      
      &__container {
        &__header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin: 0 24px;
          
          .settings-input {
            width: fit-content;
          }
        }
        canvas { 
          margin: 0 24px;
        }
      }
    }
  }
</style>